import React from "react";
import { Info } from "lucide-react";
import { CreateTimeoffRuleRequestType } from "../../../types/request/timeoff";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { TimeoffPolicyType } from "../../../types";

export type BasicInfoStepProps = {
    formData: CreateTimeoffRuleRequestType;
    setFormData: React.Dispatch<React.SetStateAction<CreateTimeoffRuleRequestType>>;
    policyTypes: TimeoffPolicyType[]
};

const BasicInfoStep = ({ formData, setFormData, policyTypes }: BasicInfoStepProps) => {
    const timeoffCategories = useSelector((state: RootState) => state.common.allTimeoffCategories);

    const handlePolicyModeChange = (policyMode: "STANDARD" | "BANK") => {
        setFormData((previousFormData) => ({
            ...previousFormData,
            policy_mode: policyMode,
            policy_config_json: {
                expire_day: previousFormData.policy_config_json?.expire_day || "30",
                expire_balance: previousFormData.policy_config_json?.expire_balance || "true",
            },
        }));
    };

    return (
        <div className="bg-white p-6 shadow rounded">
            <h2 className="text-xl font-semibold mb-2">Basic Info</h2>

            {/* Policy Name */}
            <div className="mb-4">
                <label className="block text-gray-700">Policy Name</label>
                <input
                    type="text"
                    className="w-full px-3 py-2 border rounded"
                    placeholder="e.g. Salary Employees"
                    value={formData.policy_name || ""}
                    onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                />
            </div>

            {/* Policy code */}
            <div className="mb-4">
                <label className="block text-gray-700">Policy Code</label>
                <input
                    type="text"
                    className="w-full px-3 py-2 border rounded"
                    placeholder="e.g. emp_salary"
                    value={formData.policy_code || ""}
                    onChange={(e) => setFormData({ ...formData, policy_code: e.target.value })}
                />
            </div>

            {/* Policy type Selection */}
            <div className="mt-4">
                <label className="block text-gray-700">Policy Type</label>
                <select
                    className="w-full px-3 py-2 border rounded"
                    value={formData.policy_type}
                    onChange={(e) => setFormData({ ...formData, policy_type: e.target.value })}
                >
                    <option value="">Select</option>
                    {policyTypes.map((type, index) => {
                        return (
                            <option value={type.code} key={index}>
                                {type.name}
                            </option>
                        );
                    })}
                </select>
            </div>

            <div className="mt-4">
                <div className="mb-1 flex items-center gap-2">
                    <label className="block text-gray-700">Policy Mode</label>
                    <TooltipIcon
                        text="STANDARD follows the normal time-off accrual, usage, and approval workflow, while BANK manages hours as a banked balance that posts directly into the timecard flow."
                    />
                </div>
                <select
                    className="w-full px-3 py-2 border rounded"
                    value={formData.policy_mode || "STANDARD"}
                    onChange={(e) => handlePolicyModeChange(e.target.value as "STANDARD" | "BANK")}
                >
                    <option value="STANDARD">STANDARD</option>
                    <option value="BANK">BANK</option>
                </select>
            </div>

            {/* Category Selection */}
            <div className="mt-4">
                <label className="block text-gray-700">Category</label>
                <select
                    className="w-full px-3 py-2 border rounded"
                    value={formData.category_code}
                    onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
                >
                    <option value="">Select</option>
                    {timeoffCategories.map((category, index) => {
                        return (
                            <option value={category.category_code} key={index}>
                                {category.category_name}
                            </option>
                        );
                    })}
                </select>
            </div>

            <div className="mt-4">
                <div className="mb-1 flex items-center gap-2">
                    <label className="block text-gray-700">Expires After X Days</label>
                    <TooltipIcon
                        text="Sets how many days unused balance remains available before it expires, counted from the start date."
                    />
                </div>
                <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 border rounded"
                    placeholder="30"
                    value={formData.policy_config_json?.expire_day || ""}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            policy_config_json: {
                                expire_day: e.target.value,
                                expire_balance: formData.policy_config_json?.expire_balance || "true",
                            },
                        })
                    }
                />
            </div>

            <div className="mt-4">
                <div className="mb-2 flex items-center gap-2">
                    <label className="block text-gray-700">Expire Balance</label>
                    <TooltipIcon
                        text="Controls whether unused time-off hours should be removed once the configured expiry period is reached."
                    />
                </div>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-gray-700">
                        <input
                            type="radio"
                            name="expire_balance"
                            checked={(formData.policy_config_json?.expire_balance || "true") === "true"}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    policy_config_json: {
                                        expire_day: formData.policy_config_json?.expire_day || "30",
                                        expire_balance: "true",
                                    },
                                })
                            }
                        />
                        Yes
                    </label>
                    <label className="flex items-center gap-2 text-gray-700">
                        <input
                            type="radio"
                            name="expire_balance"
                            checked={formData.policy_config_json?.expire_balance === "false"}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    policy_config_json: {
                                        expire_day: formData.policy_config_json?.expire_day || "30",
                                        expire_balance: "false",
                                    },
                                })
                            }
                        />
                        No
                    </label>
                </div>
            </div>
        </div>
    );
};

const TooltipIcon = ({ text }: { text: string }) => (
    <div className="group relative flex items-center">
        <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center text-slate-400 transition hover:text-slate-600 focus:outline-none focus:text-slate-600"
            aria-label={text}
        >
            <Info size={14} />
        </button>
        <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden w-72 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs leading-5 text-white shadow-lg group-hover:block group-focus-within:block">
            {text}
        </div>
    </div>
);

export default BasicInfoStep;
